import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Autocomplete, TextField, Chip, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const HwQuestionPageRangeSelector = ({
    textbook = null,
    numPages = null,
    showTextbookSelector = false,
    textbooks = [],
    pdfUrl = null,
    onExtract,
    onError,
    compact = false
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [chosenTextbook, setChosenTextbook] = useState(textbook);
    const [pdfNumPages, setPdfNumPages] = useState(numPages);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pageRangeInput, setPageRangeInput] = useState('');
    const [selectedPages, setSelectedPages] = useState([]);
    const [extracting, setExtracting] = useState(false);
    const [useReasoningForAnswers, setUseReasoningForAnswers] = useState(false);

    // Memoize pdf.js options to prevent re-triggering Document load on every render.
    // disableAutoFetch stops pdf.js from pre-downloading all pages after the first loads.
    const pdfOptions = React.useMemo(() => ({
        disableAutoFetch: true,
        disableStream: false,
    }), []);

    // Update state when props change
    useEffect(() => {
        if (textbook) {
            setChosenTextbook(textbook);
        }
    }, [textbook]);

    useEffect(() => {
        if (numPages) {
            setPdfNumPages(numPages);
        }
    }, [numPages]);

    // Determine effective values
    const effectivePdfUrl = pdfUrl || chosenTextbook?.file_url;
    const effectiveTextbook = chosenTextbook || textbook;
    const effectiveNumPages = pdfNumPages || numPages;

    const onDocumentLoadSuccess = ({ numPages }) => {
        setPdfNumPages(numPages);
        setPdfLoading(false);
    };

    const parsePageRange = () => {
        if (!pageRangeInput.trim()) {
            onError('Please enter a page range');
            return;
        }

        if (!effectiveNumPages) {
            onError('Please wait for PDF to load');
            return;
        }

        try {
            const pages = [];
            const parts = pageRangeInput.split(',');

            for (const part of parts) {
                const trimmed = part.trim();

                if (trimmed.includes('-')) {
                    // Range like "1-5"
                    const [start, end] = trimmed.split('-').map(Number);
                    if (isNaN(start) || isNaN(end) || start > end) {
                        throw new Error('Invalid range');
                    }
                    for (let i = start; i <= end; i++) {
                        if (i >= 1 && i <= effectiveNumPages && !pages.includes(i)) {
                            pages.push(i);
                        }
                    }
                } else {
                    // Single page
                    const pageNum = Number(trimmed);
                    if (isNaN(pageNum)) {
                        throw new Error('Invalid page number');
                    }
                    if (pageNum >= 1 && pageNum <= effectiveNumPages && !pages.includes(pageNum)) {
                        pages.push(pageNum);
                    }
                }
            }

            if (pages.length === 0) {
                throw new Error('No valid pages found');
            }

            // Sort pages
            pages.sort((a, b) => a - b);
            setSelectedPages(pages);
        } catch (error) {
            onError('Invalid page range format. Use format like "1-5" or "1,3,5,7-10"');
        }
    };

    const removePage = (pageNum) => {
        setSelectedPages(selectedPages.filter((p) => p !== pageNum));
    };

    const handleExtract = async () => {
        if (!effectiveTextbook) {
            onError('Please select a textbook');
            return;
        }

        if (selectedPages.length === 0) {
            onError('Please select at least one page');
            return;
        }

        setExtracting(true);

        try {
            // Convert each PDF page to image in the browser
            const pageImages = [];

            for (const pageNum of selectedPages) {
                // Find the canvas for this specific page
                const pageContainer = document.querySelector(`.preview-page-${pageNum}`);

                if (!pageContainer) {
                    console.warn(`Page ${pageNum} not found in preview`);
                    continue;
                }

                const canvas = pageContainer.querySelector('.react-pdf__Page__canvas');

                if (!canvas) {
                    console.warn(`Canvas not found for page ${pageNum}`);
                    continue;
                }

                // Convert canvas to base64
                const base64Image = canvas.toDataURL('image/png').split(',')[1];
                pageImages.push({
                    page: pageNum,
                    image: base64Image,
                });
            }

            if (pageImages.length === 0) {
                onError('No pages could be converted to images. Please wait for pages to load.');
                setExtracting(false);
                return;
            }

            // Check payload size before sending (server has 10mb limit)
            const payload = {
                textbook_id: effectiveTextbook.id,
                cropped_images: pageImages,
                is_full_page: true,
            };
            const payloadSize = new Blob([JSON.stringify(payload)]).size;
            const maxSize = 10 * 1024 * 1024; // 10mb in bytes

            if (payloadSize > maxSize) {
                const sizeMB = (payloadSize / (1024 * 1024)).toFixed(2);
                onError(`Payload too large (${sizeMB}MB). Maximum is 10MB. Please select fewer pages (currently ${pageImages.length} pages) or reduce image quality.`);
                setExtracting(false);
                return;
            }

            console.log(`Sending payload: ${(payloadSize / (1024 * 1024)).toFixed(2)}MB for ${pageImages.length} pages`);

            // Send page images to server (using crop endpoint with full-page flag)
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/extract/crop`,
                {
                    textbook_id: effectiveTextbook.id,
                    cropped_images: pageImages,
                    is_full_page: true, // Indicates these are full pages, not individual questions
                    use_reasoning_for_answers: useReasoningForAnswers,
                },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            // Call onExtract with the extraction job ID
            if (onExtract) {
                onExtract(response.data.extraction_job_id);
            }
        } catch (error) {
            console.error('Extract error:', error);
            onError(error.response?.data?.error || 'Failed to start extraction');
        } finally {
            setExtracting(false);
        }
    };

    return (
        <Box>
            <Typography variant="h5" fontWeight="bold" mb={3}>
                Extract Questions from Page Range
            </Typography>

            <Box display="flex" flexDirection="column" gap={3} mb={3}>
                {/* Textbook Selector (conditional) */}
                {showTextbookSelector && (
                    <Autocomplete
                        disablePortal
                        options={textbooks}
                        sx={{ width: 400 }}
                        value={chosenTextbook}
                        renderInput={(params) => <TextField {...params} label="Choose Textbook" />}
                        onChange={(event, newValue) => {
                            setChosenTextbook(newValue);
                            setPdfNumPages(null);
                            setSelectedPages([]);
                            setPageRangeInput('');
                            setPdfLoading(true);
                        }}
                    />
                )}

                {effectiveTextbook && (
                    <>
                        {/* Hidden PDF loader to get page count (if needed) */}
                        {showTextbookSelector && (
                            <Box sx={{ display: 'none' }}>
                                <Document file={chosenTextbook?.file_url} onLoadSuccess={onDocumentLoadSuccess}>
                                    <Page pageNumber={1} />
                                </Document>
                            </Box>
                        )}

                        {effectiveNumPages && (
                            <Box>
                                <Typography variant="body2" mb={1}>
                                    Textbook has {effectiveNumPages} pages
                                </Typography>
                                <Box display="flex" gap={2} alignItems="center">
                                    <TextField
                                        label="Page Range"
                                        placeholder="e.g., 1-5 or 1,3,5,7-10"
                                        value={pageRangeInput}
                                        onChange={(e) => setPageRangeInput(e.target.value)}
                                        sx={{ width: 300 }}
                                        helperText="Enter pages as ranges (1-5) or comma-separated (1,3,5)"
                                    />
                                    <Button variant="contained" color="primary" onClick={parsePageRange}>
                                        Add Pages
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Selected Pages */}
            {selectedPages.length > 0 && (
                <Box mt={4}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        Selected Pages ({selectedPages.length})
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                        {selectedPages.map((pageNum) => (
                            <Chip
                                key={pageNum}
                                label={`Page ${pageNum}`}
                                onDelete={() => removePage(pageNum)}
                                color="primary"
                            />
                        ))}
                    </Box>

                    {/* Preview of selected pages - render ALL pages for conversion */}
                    {effectiveTextbook && (
                        <Box>
                            <Typography variant="h6" fontWeight="bold" mb={2}>
                                Preview ({selectedPages.length} pages will be converted to images)
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={3}>
                                {selectedPages.map((pageNum) => (
                                    <Box
                                        key={pageNum}
                                        sx={{
                                            border: `1px solid ${colors.primary[400]}`,
                                            borderRadius: '4px',
                                            padding: '8px',
                                        }}
                                    >
                                        <Typography variant="caption" mb={1} display="block">
                                            Page {pageNum}
                                        </Typography>
                                        <Document file={effectivePdfUrl} options={pdfOptions}>
                                            <Page
                                                pageNumber={pageNum}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                                width={200}
                                                data-page-number={pageNum}
                                                className={`preview-page-${pageNum}`}
                                                loading={
                                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px" width="200px">
                                                        <CircularProgress color="inherit" size={24} />
                                                    </Box>
                                                }
                                            />
                                        </Document>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Box mt={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useReasoningForAnswers}
                                    onChange={(e) => setUseReasoningForAnswers(e.target.checked)}
                                />
                            }
                            label="Use reasoning model for answers (slower but more accurate for math/science)"
                            sx={{ mb: 2, display: 'block' }}
                        />
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={handleExtract}
                            disabled={extracting}
                        >
                            {extracting
                                ? 'Starting Extraction...'
                                : `Extract Questions from ${selectedPages.length} Page${
                                      selectedPages.length > 1 ? 's' : ''
                                  }`}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default HwQuestionPageRangeSelector;
